import { useCallback, useEffect, useRef, useState } from 'react'
import {
  detectCapability,
  getTimingConfig,
  isStandalone,
  readDismissalState,
  recordCooldownDismissal,
  recordInstalled,
  recordSessionDismissal,
  type InstallCapability,
} from './install-state'

/** Chromium's deferred install event — not in the DOM lib types yet. */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt(): Promise<void>
}

export interface InstallPromptState {
  /** Whether a notification may be shown right now (capability + timing + dismissal). */
  visible: boolean
  /** Which installation story fits this browser. */
  capability: InstallCapability
  /** StockWise is already running installed. */
  installed: boolean
  /** Fire the native dialog — must be called from a user gesture (the Install click). */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
  /** "Maybe later" — cooldown on this device. */
  defer: () => void
  /** Close button — quiet for the rest of this session. */
  dismiss: () => void
  /** Close the notification without persisting anything (used after install). */
  hide: () => void
}

export interface UseInstallPromptOptions {
  /** Gate on login: the prompt never appears before the user is signed in. */
  enabled: boolean
  /** Path check — pass false on POS/checkout/payment routes. */
  allowed?: boolean
}

/** How long a click waits for a late `beforeinstallprompt` before giving up. */
const INSTALL_EVENT_WAIT_MS = 800

/**
 * The install event can land a beat after a click on a cold load. A short
 * wait turns that click into a real install instead of a instructions dialog —
 * Chromium's transient user activation outlives this window, so the
 * subsequent `prompt()` still counts as user-triggered.
 *
 * The hook's own listener was registered at mount, so it always runs first
 * and has stored the event by the time ours fires.
 */
function waitForDeferredInstallEvent(
  ref: { current: BeforeInstallPromptEvent | null },
  ms: number,
): Promise<BeforeInstallPromptEvent | null> {
  return new Promise((resolve) => {
    if (ref.current) {
      resolve(ref.current)
      return
    }
    let settled = false
    let timer = 0
    const settle = () => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      window.removeEventListener('beforeinstallprompt', settle)
      resolve(ref.current)
    }
    window.addEventListener('beforeinstallprompt', settle)
    timer = window.setTimeout(settle, ms)
  })
}

const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
}

/**
 * Captures Chromium's `beforeinstallprompt`, applies the timing and
 * dismissal rules, and exposes the three actions the notification offers.
 *
 * The native dialog is only ever triggered from `install()`, i.e. from a
 * click — browsers ignore it otherwise.
 */
export function useInstallPrompt({ enabled, allowed = true }: UseInstallPromptOptions): InstallPromptState {
  const configRef = useRef(getTimingConfig())
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null)

  const [nativePromptAvailable, setNativePromptAvailable] = useState(false)
  const [installed, setInstalled] = useState(() => isStandalone())
  const [blocked, setBlocked] = useState(() =>
    typeof window === 'undefined'
      ? true
      : readDismissalState(window.localStorage, window.sessionStorage, Date.now()).blocked,
  )
  const [eligible, setEligible] = useState(false)

  // 1. Capture the install event; drop it when the app gets installed.
  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      event.preventDefault()
      deferredRef.current = event as BeforeInstallPromptEvent
      setNativePromptAvailable(true)
    }
    const onInstalled = () => {
      deferredRef.current = null
      setNativePromptAvailable(false)
      setInstalled(true)
      if (typeof window !== 'undefined') recordInstalled(window.localStorage)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  // 2. Re-read dismissal state when something around us changes.
  const refreshBlocked = useCallback(() => {
    setBlocked(readDismissalState(window.localStorage, window.sessionStorage, Date.now()).blocked)
  }, [])

  // 3. Timing: no prompt on first paint, only after `promptDelayMs` of an
  //    engaged, signed-in session — and never on an interruptible route.
  useEffect(() => {
    if (!enabled || !allowed || blocked || installed) {
      setEligible(false)
      return
    }
    const timer = window.setTimeout(() => setEligible(true), configRef.current.promptDelayMs)
    return () => window.clearTimeout(timer)
  }, [enabled, allowed, blocked, installed])

  // 4. If the app is installed while we are open (browser menu path), close us.
  useEffect(() => {
    if (installed || readDismissalState(window.localStorage, window.sessionStorage, Date.now()).blocked) {
      setEligible(false)
    }
  }, [installed])

  const capability: InstallCapability = detectCapability(
    { standalone: installed || isStandalone(), userAgent: navigator.userAgent },
    nativePromptAvailable,
  )

  const visible = eligible && !blocked && !installed && capability !== 'none'

  const install = useCallback(async () => {
    const deferred = deferredRef.current ?? (await waitForDeferredInstallEvent(deferredRef, INSTALL_EVENT_WAIT_MS))
    if (!deferred) {
      // No native dialog available: the caller shows guidance instead.
      return 'unavailable' as const
    }
    try {
      await deferred.prompt()
      const choice = await deferred.userChoice
      deferredRef.current = null
      if (choice.outcome === 'accepted') {
        setInstalled(true)
        recordInstalled(window.localStorage)
      } else {
        // User dismissed the OS dialog — respect it with a full cooldown.
        recordCooldownDismissal(window.localStorage, Date.now(), configRef.current)
        setBlocked(true)
      }
      return choice.outcome
    } catch {
      return 'dismissed' as const
    }
  }, [])

  const defer = useCallback(() => {
    recordCooldownDismissal(window.localStorage, Date.now(), configRef.current)
    setBlocked(true)
    setEligible(false)
  }, [])

  const dismiss = useCallback(() => {
    recordSessionDismissal(window.sessionStorage)
    setBlocked(true)
    setEligible(false)
  }, [])

  const hide = useCallback(() => setEligible(false), [])

  // Keep `blocked` in sync with other tabs on this device.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key && Object.values(['stockwise:pwa:installed', 'stockwise:pwa:opt-out']).includes(event.key)) {
        refreshBlocked()
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [refreshBlocked])

  return { visible, capability, installed, install, defer, dismiss, hide }
}
